import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './SectorCalendar.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_SHORT = [
  'Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec',
];
const DAY_HEADERS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function buildYears() {
  const cur = new Date().getFullYear();
  return [cur - 2, cur - 1, cur, cur + 1].filter(y => y >= 2022);
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatNumber(num) {
  if (num === null || num === undefined) return '—';
  const absNum = Math.abs(Math.round(num));
  const formatted = new Intl.NumberFormat('en-IN').format(absNum);
  return num < 0 ? `-${formatted}` : formatted;
}

function formatDate(dateStr, day, month, year) {
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d)) return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SectorCalendar() {
  const now = new Date();
  const YEARS = buildYears();

  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [sectors, setSectors]         = useState([]);   // string[]
  const [sectorsData, setSectorsData] = useState([]);   // [{industry, totalCsValue, stockCount}]
  const [selectedSector, setSelectedSector] = useState(null);

  const [calData, setCalData]     = useState(null);  // full calendar API response
  const [selectedDay, setSelectedDay] = useState(null);

  const [secLoading, setSecLoading] = useState(false);
  const [calLoading, setCalLoading] = useState(false);

  const [sectorSort, setSectorSort] = useState('desc'); // 'desc' | 'asc' | 'alpha'
  const [stockSort, setStockSort]   = useState('desc'); // 'desc' | 'asc' | 'alpha'
  const [copiedStocks, setCopiedStocks] = useState(false);

  const [tooltip, setTooltip] = useState({
    visible: false, x: 0, y: 0,
    day: null, value: 0, qty: 0, date: '', stockCount: 0,
  });

  const isFirstLoad   = useRef(true);
  const sectorListRef = useRef(null);

  // ── Load sector list ───────────────────────────────────────────────────────
  const loadSectors = useCallback(async (currentSector) => {
    setSecLoading(true);
    try {
      const { data } = await api.get(`/sector-analysis/sectors?year=${year}&month=${month}`);
      const secs     = (data.sectors || []);
      const secsData = (data.sectorsData || []);
      setSectors(secs);
      setSectorsData(secsData);

      if (isFirstLoad.current && secs.length > 0) {
        isFirstLoad.current = false;
        const first = secs[0];
        setSelectedSector(first);
        return first;
      }
      return currentSector;
    } catch (e) {
      console.error('loadSectors error', e);
      return currentSector;
    } finally {
      setSecLoading(false);
    }
  }, [year, month]);

  // ── Load calendar data ─────────────────────────────────────────────────────
  const loadCalendar = useCallback(async (sector) => {
    if (!sector) return;
    setCalLoading(true);
    setSelectedDay(null);
    try {
      const { data } = await api.get(
        `/sector-analysis/calendar?sector=${encodeURIComponent(sector)}&year=${year}&month=${month}`
      );
      setCalData(data);
    } catch (e) {
      console.error('loadCalendar error', e);
      setCalData(null);
    } finally {
      setCalLoading(false);
    }
  }, [year, month]);

  // ── Initial mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const first = await loadSectors(null);
      if (first) await loadCalendar(first);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Year / month change ────────────────────────────────────────────────────
  const handleYearChange = (y) => {
    setYear(y);
    setCalData(null);
    setSelectedDay(null);
  };
  const handleMonthChange = (m) => {
    setMonth(m);
    setCalData(null);
    setSelectedDay(null);
  };

  const getAdjacentMonth = (offset) => {
    const target = new Date(Date.UTC(year, month - 1 + offset, 1));
    return {
      year: target.getUTCFullYear(),
      month: target.getUTCMonth() + 1,
      label: `${MONTHS_SHORT[target.getUTCMonth()]} ${target.getUTCFullYear()}`,
    };
  };

  const navigateMonth = (offset) => {
    const target = getAdjacentMonth(offset);
    setYear(target.year);
    setMonth(target.month);
    setCalData(null);
    setSelectedDay(null);
  };

  // Re-fetch when year/month change (after mount)
  useEffect(() => {
    if (isFirstLoad.current) return;
    (async () => {
      setSecLoading(true);
      try {
        const { data } = await api.get(`/sector-analysis/sectors?year=${year}&month=${month}`);
        setSectors(data.sectors || []);
        setSectorsData(data.sectorsData || []);
      } catch (e) {
        console.error(e);
      } finally {
        setSecLoading(false);
      }
      if (selectedSector) await loadCalendar(selectedSector);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // ── Select sector ──────────────────────────────────────────────────────────
  const handleSelectSector = async (sector) => {
    setSelectedSector(sector);
    setCalData(null);
    setSelectedDay(null);
    setTimeout(() => {
      const el = sectorListRef.current?.querySelector(`[data-sector="${CSS.escape(sector)}"]`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
    await loadCalendar(sector);
  };

  // ── Calendar grid ──────────────────────────────────────────────────────────
  const buildCalendarCells = () => {
    const firstDay    = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;

    const dataMap = {};
    (calData?.dailyData || []).forEach(d => { dataMap[d.day] = d; });

    const cells = [];

    DAY_HEADERS.forEach(lbl => cells.push({ type: 'header', label: lbl }));

    for (let i = 0; i < startOffset; i++) cells.push({ type: 'empty' });

    for (let day = 1; day <= daysInMonth; day++) {
      const d = dataMap[day];
      const hasData = !!(d && d.csValue !== undefined && d.csValue !== 0);
      cells.push({ type: 'day', day, hasData, dayData: d || null });
    }

    const total = startOffset + daysInMonth;
    const rem   = total % 7 === 0 ? 0 : 7 - (total % 7);
    for (let i = 0; i < rem; i++) cells.push({ type: 'empty' });

    return cells;
  };

  // ── Day click ──────────────────────────────────────────────────────────────
  const handleDayClick = (day) => {
    setSelectedDay(prev => prev === day ? null : day);
  };

  // ── Tooltip ────────────────────────────────────────────────────────────────
  const handleMouseMove = (e, dayData, day) => {
    const tw = 230, th = 165;
    let x = e.clientX + 15;
    let y = e.clientY + 15;
    if (x + tw > window.innerWidth)  x = e.clientX - tw - 10;
    if (y + th > window.innerHeight) y = e.clientY - th - 10;
    setTooltip({
      visible: true, x, y, day,
      value: dayData.csValue,
      qty: dayData.csQty || 0,
      date: dayData.date || '',
      stockCount: dayData.stockCount || 0,
    });
  };
  const handleMouseLeave = () => setTooltip(t => ({ ...t, visible: false }));

  // ── Derived: right-panel stocks ────────────────────────────────────────────
  const rightStocks = (() => {
    if (!calData || !selectedSector) return null;
    if (selectedDay) {
      const dd = (calData.dailyData || []).find(d => d.day === selectedDay);
      if (dd?.stocks?.length) {
        return {
          stocks: dd.stocks,
          label: `${selectedSector} — ${selectedDay} ${MONTHS[month - 1]} ${year}`,
        };
      }
    }
    if (calData.stockBreakdown?.length) {
      return {
        stocks: calData.stockBreakdown,
        label: `${selectedSector} — ${MONTHS[month - 1]} ${year}`,
      };
    }
    return { stocks: [], label: `${selectedSector} — ${MONTHS[month - 1]} ${year}` };
  })();

  // ── Sector value map ───────────────────────────────────────────────────────
  const valueMap = {};
  sectorsData.forEach(s => { valueMap[s.industry] = s; });

  const copySymbolsForTV = () => {
    if (!rightStocks || !rightStocks.stocks || rightStocks.stocks.length === 0) return;
    const text = rightStocks.stocks.map((s) => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedStocks(true);
    setTimeout(() => setCopiedStocks(false), 2000);
  };

  const cells = buildCalendarCells();

  // ── Peak high / low days ───────────────────────────────────────────────────
  const peakDays = (() => {
    const daily = (calData?.dailyData || []).filter(d => d.csValue !== undefined && d.csValue !== 0);
    if (daily.length === 0) return { highDay: null, lowDay: null };
    const highDay = daily.reduce((a, b) => b.csValue > a.csValue ? b : a).day;
    const lowDay  = daily.reduce((a, b) => b.csValue < a.csValue ? b : a).day;
    return { highDay, lowDay };
  })();

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="Sector Calendar">
      <div className="sc-root">

        {/* ── 3-Column Body ─────────────────────────────────────────── */}
        <div className="sc-body">

          {/* LEFT: Sector List */}
          <aside className="sc-sidebar" ref={sectorListRef}>
            <div className="sc-sidebar-hdr">
              <span className="sc-sidebar-title">Sectors</span>
              <span className="sc-count-badge">{sectors.length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px 4px' }}>
              {[{ key: 'desc', label: '\u2193 CS' }, { key: 'asc', label: '\u2191 CS' }, { key: 'alpha', label: 'A\u2013Z' }].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSectorSort(opt.key)}
                  style={{
                    fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                    borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                    border: sectorSort === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                    background: sectorSort === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                    color: sectorSort === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="sc-sector-list">
              {secLoading ? (
                <div className="sc-list-loading"><div className="sc-spinner" />Loading…</div>
              ) : sectors.length === 0 ? (
                <div className="sc-no-data">No sectors available</div>
              ) : [...sectors].sort((a, b) => {
                  if (sectorSort === 'alpha') return a.localeCompare(b);
                  const aCS = valueMap[a]?.totalCsValue ?? 0;
                  const bCS = valueMap[b]?.totalCsValue ?? 0;
                  return sectorSort === 'desc' ? bCS - aCS : aCS - bCS;
                }).map(sec => {
                const sd  = valueMap[sec];
                const cs  = sd?.totalCsValue ?? 0;
                const cnt = sd?.stockCount ?? 0;
                const cls = cs > 0 ? 'pos' : cs < 0 ? 'neg' : 'none';
                return (
                  <div
                    key={sec}
                    data-sector={sec}
                    className={`sc-sector-item${selectedSector === sec ? ' selected' : ''}`}
                    onClick={() => handleSelectSector(sec)}
                  >
                    <div className="sc-sector-name">{sec}</div>
                    <div className="sc-sector-meta">
                      <span className="sc-sector-count">{cnt} stocks</span>
                      <span className={`sc-sector-cs ${cls}`}>{formatNumber(cs)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>

          {/* CENTER: Main */}
          <main className="sc-main">

            {/* Year + Month selectors */}
            <div className="sc-selectors-row">
              <div className="sc-selector-group">
                <span className="sc-selector-label">Year</span>
                <select
                  className="sc-selector-select"
                  value={year}
                  onChange={(event) => handleYearChange(parseInt(event.target.value, 10))}
                >
                  {[...new Set([...YEARS, year])].sort((a, b) => a - b).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="sc-sel-divider" />
              <div className="sc-selector-group">
                <span className="sc-selector-label">Month</span>
                <select
                  className="sc-selector-select"
                  value={month}
                  onChange={(event) => handleMonthChange(parseInt(event.target.value, 10))}
                >
                  {MONTHS_SHORT.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="sc-summary-section">
              <div className="sc-summary-card sc-card-1">
                <div className="sc-summary-label">Total CS Value</div>
                <div className={`sc-summary-value${calData ? (calData.totalValue >= 0 ? ' positive' : ' negative') : ''}`}>
                  {calData ? formatNumber(calData.totalValue || 0) : '—'}
                </div>
              </div>
              <div className="sc-summary-card sc-card-2">
                <div className="sc-summary-label">Stock Count</div>
                <div className="sc-summary-value">{calData ? (calData.totalStockCount || 0) : '—'}</div>
              </div>
              <div className="sc-summary-card sc-card-3">
                <div className="sc-summary-label">Highest Day</div>
                {calData?.highestDay ? (
                  <>
                    <div className={`sc-summary-value ${calData.highestDay.csValue >= 0 ? 'positive' : 'negative'}`}>
                      {formatNumber(calData.highestDay.csValue)}
                    </div>
                    <div className="sc-summary-date">
                      {formatDate(calData.highestDay.date, calData.highestDay.day, month, year)}
                    </div>
                  </>
                ) : <div className="sc-summary-value">—</div>}
              </div>
              <div className="sc-summary-card sc-card-4">
                <div className="sc-summary-label">Lowest Day</div>
                {calData?.lowestDay ? (
                  <>
                    <div className={`sc-summary-value ${calData.lowestDay.csValue >= 0 ? 'positive' : 'negative'}`}>
                      {formatNumber(calData.lowestDay.csValue)}
                    </div>
                    <div className="sc-summary-date">
                      {formatDate(calData.lowestDay.date, calData.lowestDay.day, month, year)}
                    </div>
                  </>
                ) : <div className="sc-summary-value">—</div>}
              </div>
            </div>

            {/* Calendar */}
            <div className="sc-calendar-section">
              <div className="sc-calendar-card">
                <div className="sc-calendar-header">
                  <div className="sc-calendar-title">
                    {MONTHS[month - 1]} {year}
                    {selectedSector && <span> — {selectedSector}</span>}
                  </div>
                  {selectedDay && (
                    <button className="sc-clear-day" onClick={() => setSelectedDay(null)}>
                      ✕ Clear day
                    </button>
                  )}
                </div>
                <div className="sc-calendar-body">
                  <div className="sc-month-nav" aria-label="Calendar month navigation">
                    <button type="button" onClick={() => navigateMonth(-1)}>
                      ‹ {getAdjacentMonth(-1).label}
                    </button>
                    <span>{MONTHS[month - 1]} {year}</span>
                    <button type="button" onClick={() => navigateMonth(1)}>
                      {getAdjacentMonth(1).label} ›
                    </button>
                  </div>
                  {calLoading ? (
                    <div className="sc-cal-placeholder">
                      <div className="sc-spinner" />Loading calendar…
                    </div>
                  ) : !selectedSector ? (
                    <div className="sc-cal-placeholder">Select a sector to view calendar</div>
                  ) : !calData ? (
                    <div className="sc-cal-placeholder">No data available</div>
                  ) : (
                    <div className="sc-calendar-grid" onMouseLeave={handleMouseLeave}>
                      {cells.map((cell, idx) => {
                        if (cell.type === 'header') {
                          return <div key={`h${idx}`} className="sc-day-header">{cell.label}</div>;
                        }
                        if (cell.type === 'empty') {
                          return <div key={`e${idx}`} className="sc-cal-cell empty" />;
                        }
                        const { day, hasData, dayData } = cell;
                        const isPos  = hasData && dayData.csValue >= 0;
                        const isSel  = selectedDay === day;
                        const isHigh = hasData && day === peakDays.highDay;
                        const isLow  = hasData && day === peakDays.lowDay;
                        let cls = 'sc-cal-cell';
                        if (hasData) cls += isPos ? ' has-data positive' : ' has-data negative';
                        if (isSel)  cls += ' selected';
                        if (isHigh) cls += ' peak-high';
                        if (isLow)  cls += ' peak-low';
                        return (
                          <div
                            key={`d${day}`}
                            className={cls}
                            onClick={() => hasData && handleDayClick(day)}
                            onMouseMove={hasData ? e => handleMouseMove(e, dayData, day) : undefined}
                          >
                            <span className="sc-cell-day">{day}</span>
                            {hasData && (
                              <div className="sc-cell-value">{formatNumber(dayData.csValue)}</div>
                            )}
                            {isHigh && <span className="sc-peak-badge high">▲</span>}
                            {isLow  && <span className="sc-peak-badge low">▼</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </main>

          {/* RIGHT: Stock Table */}
          <aside className="sc-table-panel">
            <div className="sc-table-hdr">
              <div className="sc-table-title">Stock Breakdown</div>
              <div className="sc-table-sector">
                {rightStocks
                  ? rightStocks.label
                  : selectedSector
                    ? `${selectedSector} — ${MONTHS[month - 1]} ${year}`
                    : '— select a sector —'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                <button
                  onClick={copySymbolsForTV}
                  disabled={!rightStocks || !rightStocks.stocks || rightStocks.stocks.length === 0}
                  title="Copy all symbols for TradingView watchlist"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.65rem', fontWeight: 600,
                    padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                    border: copiedStocks ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                    background: copiedStocks ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                    color: copiedStocks ? '#6ee7b7' : '#10b981',
                    transition: 'all 0.2s',
                    opacity: rightStocks && rightStocks.stocks && rightStocks.stocks.length > 0 ? 1 : 0.4,
                  }}
                >
                  {copiedStocks ? '✓ Copied!' : '⧉ Copy'}
                </button>
                {[{ key: 'desc', label: '\u2193 CS' }, { key: 'asc', label: '\u2191 CS' }, { key: 'alpha', label: 'A\u2013Z' }].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setStockSort(opt.key)}
                    style={{
                      fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                      borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                      border: stockSort === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                      background: stockSort === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                      color: stockSort === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>            </div>
            <div className="sc-table-scroll">
              <table className="sc-stocks-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th className="right">CS Value</th>
                  </tr>
                </thead>
                <tbody>
                  {!selectedSector ? (
                    <tr><td colSpan={2}><div className="sc-table-empty"><div className="sc-empty-icon">👈</div>Select a sector</div></td></tr>
                  ) : calLoading ? (
                    <tr><td colSpan={2}><div className="sc-table-empty"><div className="sc-spinner" style={{ margin: '0 auto 6px' }} />Loading…</div></td></tr>
                  ) : !rightStocks || rightStocks.stocks.length === 0 ? (
                    <tr><td colSpan={2}><div className="sc-table-empty"><div className="sc-empty-icon">📊</div>No stock data</div></td></tr>
                  ) : rightStocks.stocks
                      .slice()
                      .sort((a, b) => {
                        if (stockSort === 'alpha') return a.symbol.localeCompare(b.symbol);
                        const aVal = a.totalCS ?? a.csValue ?? 0;
                        const bVal = b.totalCS ?? b.csValue ?? 0;
                        return stockSort === 'desc' ? bVal - aVal : aVal - bVal;
                      })
                      .map((s, i) => {
                        const val = s.totalCS ?? s.csValue ?? 0;
                        const cats = s.categories || (s.category ? [s.category] : []);
                        let col = 'fo';
                        if (cats.some(c => c === 'N50' || c === 'nifty50')) col = 'nifty50';
                        else if (cats.some(c => c === 'Others' || c === 'others')) col = 'cash';
                        const calUrl = `/cs-calendar?symbol=${encodeURIComponent(s.symbol)}&year=${year}&month=${month}&collection=${col}`;
                        return (
                          <tr key={i}>
                            <td className="sc-td-symbol">
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {s.symbol}
                                <a
                                  href={`https://in.tradingview.com/chart/?symbol=NSE%3A${s.symbol}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`View ${s.symbol} on TradingView`}
                                  style={{ color: '#60a5fa', lineHeight: 1, flexShrink: 0 }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                  </svg>
                                </a>
                                <a
                                  href={calUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`View ${s.symbol} CS Calendar`}
                                  style={{ color: '#a78bfa', lineHeight: 1, flexShrink: 0 }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </a>
                              </span>
                            </td>
                            <td className={`right ${val >= 0 ? 'sc-td-pos' : 'sc-td-neg'}`}>
                              {formatNumber(val)}
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </aside>

        </div>{/* /sc-body */}

        {/* Tooltip */}
        {tooltip.visible && (
          <div className="sc-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
            <div className="sc-tooltip-header">{selectedSector || 'SECTOR'}</div>
            <div className="sc-tooltip-row">
              <span className="sc-tooltip-label">Date:</span>
              <span className="sc-tooltip-value">{formatDate(tooltip.date, tooltip.day, month, year)}</span>
            </div>
            <div className="sc-tooltip-row">
              <span className="sc-tooltip-label">Stock Count:</span>
              <span className="sc-tooltip-value">
                {tooltip.stockCount > 0 ? `${tooltip.stockCount} stocks` : '—'}
              </span>
            </div>
            <div className="sc-tooltip-row">
              <span className="sc-tooltip-label">CS Value:</span>
              <span className={`sc-tooltip-value ${tooltip.value >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(tooltip.value)}
              </span>
            </div>
            <div className="sc-tooltip-row">
              <span className="sc-tooltip-label">CS Qty:</span>
              <span className="sc-tooltip-value">{formatNumber(tooltip.qty)}</span>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}
